import { createAction } from "@reduxjs/toolkit";

import { IAddressValidationResult } from "Model/Omikron/IAddressValidationResult";
import { IDuplicateCheckResult } from "Model/Omikron/IDuplicateCheckResult";
import { ErrorAction } from "Model/State/ErrorAction";
import { INotification } from "Model/State/INotification";
import { IProject } from "Model/Storage/IProject";
import { ProjectCookie } from "Utils/ProjectCookie";

function withPayloadType<T>() {
  return (payload?: T, showNotification = false, withExport = false) => ({
    payload,
    meta: { showNotification, withExport }
  });
}

// #region PROJECTS
// RETRIEVE
export const projectsRetrieveStart = createAction(`projects/retrieve/start`);
export const projectsRetrieveNext = createAction<ProjectCookie>(`projects/retrieve/next`);
export const projectsRetrieveSuccess = createAction<boolean>(`projects/retrieve/success`);
export const projectsRetrieveError = createAction<ErrorAction>(`projects/retrieve/error`);

export const projectsRetrieveSinceStart = createAction<boolean>(`projects/retrieve/since/start`);
export const projectsRetrieveSinceSuccess = createAction(`projects/retrieve/since/success`);
export const projectsRetrieveSinceError = createAction<ErrorAction>(`projects/retrieve/since/error`);

export const projectsRetrieveExtendedSet = createAction<boolean>(`projects/retrieve/extended/set`);

// AUTOMATIC REFRESH
export const projectsRetrieveAutomatic = createAction(`projects/retrieve/automatic`);
export const projectsLastRequestOnSet = createAction<string>(`projects/lastrequeston/set`);
export const projectsLastRequestIdListSet = createAction<string[]>(`projects/lastrequestidlist/set`);

// UPDATE
export const projectsUpdateStart = createAction(`projects/update/start`, withPayloadType<IProject[]>());
export const projectsUpdateSuccess = createAction(`projects/update/success`, withPayloadType<IProject[]>());
export const projectsUpdateError = createAction<ErrorAction>(`projects/update/error`);

// FILTER
export const projectsFilterLoad = createAction<void>(`projects/filter/load`);
export const projectsFilterExpressionAdd = createAction<string>(`projects/filter/expression/add`);
export const projectsFilterExpressionRemove = createAction(
  `projects/filter/expression/remove`,
  (fieldName: string, search: string) => ({ payload: { fieldName, search } })
);
export const projectsFilterExpressionSet = createAction<string>(`projects/filter/expression/set`);
export const projectsFilterKeySet = createAction<string>(`projects/filter/key/set`);
export const projectsFilterStart = createAction(`projects/filter/start`);
export const projectsFilterIdListSet = createAction<string[]>(`projects/filter/idlist/set`);
export const projectsFilterCountSet = createAction<[string, number][]>(`projects/filter/count/set`);

// SORT
export const projectsSortStart = createAction<[string, boolean][]>(`projects/sort/start`);
export const projectsSortSuccess = createAction<string[]>(`projects/sort/success`);
export const projectsSortColumnsSet = createAction<[string, boolean][]>(`projects/sort/columns/set`);

// IMPORT / SYNC
export const importStart = createAction<void>(`projects/import/start`);
export const importSuccess = createAction<void>(`projects/import/success`);
export const importError = createAction<ErrorAction>(`projects/import/error`);
export const syncStart = createAction(`projects/sync/start`, (id: string, userName: string, lastXMonths: number) => ({
  payload: { id, userName, lastXMonths }
}));
export const syncSuccess = createAction(`projects/sync/success`);
export const syncError = createAction(`projects/sync/error`);

// EXPORT
export const projectsExportStart = createAction(`projects/export/start`);
export const projectsExportNext = createAction(`projects/export/next`);
export const projectsExportSuccess = createAction<string>(`projects/export/success`);
export const projectsExportError = createAction<ErrorAction>(`projects/export/error`);
export const projectsExportDialog = createAction<boolean>(`projects/export/dialog`);

// CLONE
export const projectsCloneStart = createAction(`projects/clone/start`);
export const projectsCloneNext = createAction(`projects/clone/next`);
export const projectsCloneSuccess = createAction(`projects/clone/success`);
export const projectsCloneError = createAction<ErrorAction>(`projects/clone/error`);
export const projectsCloneDialog = createAction<boolean>(`projects/clone/dialog`);

// DELETE
export const projectsDeleteStart = createAction(`projects/delete/start`);
export const projectsDeleteNext = createAction<string>(`projects/delete/next`);
export const projectsDeleteSuccess = createAction(`projects/delete/success`);
export const projectDeleteSuccess = createAction<string>(`project/delete/success`);
export const projectsDeleteError = createAction<ErrorAction>(`projects/delete/error`);
export const projectsDeleteDialog = createAction<boolean>(`projects/delete/dialog`);

// RELEVANCE
export const projectsRelevanceStart = createAction(`projects/relevance/start`, withPayloadType<string[]>());
export const projectsRelevanceSuccess = createAction(`projects/relevance/success`);
export const projectsRelevanceError = createAction<ErrorAction>(`projects/relevance/error`);

// COPY LINK
export const projectsCopyLinkStart = createAction(`projects/copylink/start`);
export const projectsCopyLinkSuccess = createAction(`projects/copylink/success`);
export const projectsCopyLinkError = createAction<ErrorAction>(`projects/copylink/error`);

export const projectsCompanyRelevanceStart = createAction(
  `projects/company/relevance/start`,
  withPayloadType<string>()
);

export const projectsCompanyRelevanceSuccess = createAction(`projects/company/relevance/success`);
export const projectsCompanyRelevanceError = createAction<ErrorAction>(`projects/company/relevance/error`);

export const projectsContactRelevanceStart = createAction(
  `projects/company/contact/relevance/start`,
  withPayloadType<string>()
);

export const projectsContactRelevanceSuccess = createAction(`projects/company/contact/relevance/success`);
export const projectsContactRelevanceError = createAction<ErrorAction>(`projects/company/contact/relevance/error`);

// STAGING
export const projectsStagingManageStart = createAction(`projects/staging/start`);
export const projectsStagingManageSuccess = createAction<Record<string, unknown>>(`projects/staging/success`);
export const projectsStagingManageError = createAction<ErrorAction>(`projects/staging/error`);
export const projectsStagingManageDialog = createAction<boolean>(`projects/staging/dialog`);
export const projectsStagingManageRemoveStart = createAction<string>(`projects/staging/remove/start`);
export const projectsStagingManageRemoveSuccess = createAction<string>(`projects/staging/remove/success`);
export const projectsStagingManageRemoveError = createAction<ErrorAction>(`projects/staging/remove/error`);
export const projectsStagingUpdateStart = createAction<string>(`projects/staging/update/start`);
export const projectsStagingUpdateSuccess = createAction<Record<string, unknown>>(`projects/staging/update/success`);
export const projectsStagingUpdateError = createAction<ErrorAction>(`projects/staging/update/error`);
export const projectsStagingMergeStart = createAction(`projects/staging/merge/start`, withPayloadType<IProject>());
export const projectsStagingMergeSuccess = createAction(`projects/staging/merge/success`, withPayloadType<string>());
export const projectsStagingMergeError = createAction<ErrorAction>(`projects/staging/merge/error`);

export const projectsAdd = createAction<IProject[]>(`projects/add`);
export const projectsUpsert = createAction<IProject[]>(`projects/upsert`);
export const projectsClear = createAction(`projects/clear`);
export const projectsSetTotalCount = createAction<number>(`projects/totalcount/set`);
// #endregion PROJECTS

// #region UI
export const uiAppInit = createAction<boolean>(`ui/app/init`);
export const uiNotificationRemove = createAction<INotification>(`ui/notification/remove`);
export const uiNotificationAdd = createAction<INotification>(`ui/notification/add`);
export const uiSetActiveEditItemId = createAction(`ui/activeedititemid/set`, withPayloadType<string>());
export const uiSetSelectedProjectIds = createAction<string[]>(`ui/selectedprojectids/set`);
export const uiSetActiveProjectId = createAction<string>(`ui/activeprojectid/set`);
export const uiSetActiveCompanyId = createAction<string>(`ui/activecompanyid/set`);
export const uiSetActiveContactId = createAction<string>(`ui/activecontactid/set`);
export const uiSetLanguage = createAction<string>(`ui/language/set`);
export const uiSetAuthorizationToken = createAction<string>(`ui/authorizationtoken/set`);
export const uiSetConfigurationReady = createAction<void>(`ui/configurationready/set`);
export const uiSetToolbarIconsOnly = createAction<boolean>(`ui/toolbarIconsOnly/set`);
export const uiSetTranslationReady = createAction<boolean>(`ui/translationsready/set`);
export const uiSetDetailsOpen = createAction<boolean>(`ui/detailsopen/set`);
export const uiStagingOpenSet = createAction<boolean>(`ui/staging/open/set`);
export const uiSetUserSettingsOpen = createAction<boolean>(`ui/usersettingsopen/set`);
export const uiUsernamesAdd = createAction<string[]>(`ui/usernames/add`);
export const uiUsernamesRemove = createAction<string[]>(`ui/usernames/remove`);
export const uiUsernamesLoad = createAction<string>(`ui/usernames/load`);
export const uiLoginUsernameSet = createAction<string>(`ui/login/username/set`);
export const uiLastOpenedPIDSet = createAction<string>(`ui/lastopenedpid/set`);
export const uiRefreshEveryTimeSet = createAction<number>(`ui/refresheverytime/set`);
export const uiRefreshEveryTimeLoad = createAction(`ui/refresheverytime/load`);
export const uiSetIsFiltering = createAction<boolean>(`ui/isfiltering/set`);
export const uiSetIsSorting = createAction<boolean>(`ui/issorting/set`);

// AUTOCOMPLETE SUGGESTIONS
export const uiAutoCompleteSuggestionsAdd = createAction<IProject[]>(`ui/autocomplete/suggestions/add`);

// #endregion

// #region OMIKRON
export const addressValidationStart = createAction(`addressvalidation/start`, withPayloadType<string[]>());
export const addressValidationSuccess = createAction(
  `addressvalidation/success`,
  withPayloadType<IAddressValidationResult[][]>()
);
export const addressValidationError = createAction<ErrorAction>(`addressvalidation/error`);
export const addressValidationDialog = createAction<boolean>(`addressvalidation/dialog`);
export const addressValidationCacheSet = createAction(`addressvalidation/cache/set`, withPayloadType<IProject>());
export const addressValidationActiveProjectIdSelect = createAction<string>(`addressvalidation/activeprojectid/select`);
export const addressValidationActiveProjectItemIdSelect = createAction<string>(
  `addressvalidation/activeprojectitemid/select`
);

// REMOVE?
export const addressValidationSuccessEmpty = createAction(`addressvalidation/success`);

// DUPLICATE CHECK
export const duplicateCheckStart = createAction(`duplicatecheck/start`, withPayloadType<string[]>());
export const duplicateCheckSuccess = createAction(
  `duplicatecheck/success`,
  withPayloadType<IDuplicateCheckResult[][]>()
);
export const duplicateCheckError = createAction<ErrorAction>(`duplicatecheck/error`);
export const duplicateCheckDialog = createAction<boolean>(`duplicatecheck/dialog`);
export const duplicateCheckCacheSet = createAction(`duplicatecheck/cache/set`, withPayloadType<IProject>());
export const duplicateCheckActiveProjectIdSelect = createAction<string>(`duplicatecheck/activeprojectid/select`);
export const duplicateCheckActiveProjectItemIdSelect = createAction<string>(
  `duplicatecheck/activeprojectitemid/select`
);
// #endregion

export const versionsGetStart = createAction(`versions/get/start`);
export const versionsGetSuccess = createAction<Record<string, string>>(`versions/get/success`);
export const versionsGetError = createAction<ErrorAction>(`versions/get/error`);

export const uiSetTheme = createAction<string>(`ui/theme/set`);
